// --- Core ---
import {FC, ReactElement} from "react";
import {FieldRenderProps} from "react-final-form";
import {FormattedMessage} from "react-intl";
// --- Material UI ---
import {
  CircularProgress,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputAdornmentProps,
  InputLabel,
  OutlinedInput,
  Skeleton,
  // eslint-disable-next-line no-restricted-imports
  TextField as MUITextField,
  TextFieldProps,
  Tooltip
} from "@mui/material";
import {ErrorOutline} from '@mui/icons-material';

interface CustomReadonlyProps {
  customHelperText?: ReactElement;
  label: ReactElement;
}

/**
 * Show a TextField as readonly where the content cannot be changed
 */
const ReadonlyTextField: FC<FieldRenderProps<string> & CustomReadonlyProps> = ({customHelperText, input, label}) => (
  <FormControl fullWidth variant="outlined">
    <InputLabel>{label}</InputLabel>
    <OutlinedInput label={label} readOnly {...input}/>
    <FormHelperText>{customHelperText}</FormHelperText>
  </FormControl>
);

interface SaniQTextFieldProps extends CustomReadonlyProps {
  endAdornment?: ReactElement;
  endAdornmentProps?: InputAdornmentProps;
  isLoading?: boolean;
  maxCharacters?: number;
  startAdornment?: ReactElement;
  startAdornmentProps?: InputAdornmentProps;
}

const TextField: FC<FieldRenderProps & TextFieldProps & SaniQTextFieldProps> = (
  {
    input,
    meta: {touched, invalid, error, submitError},
    maxCharacters,
    startAdornment,
    startAdornmentProps,
    endAdornment,
    endAdornmentProps,
    InputProps,
    customHelperText,
    isLoading,
    children,
    ...custom
  }
) => {
  let maxCharacterAdornment;
  let maxCharactersError = false;
  let startAdornmentComponent;
  let endAdornmentComponent;
  let errorText;

  if (maxCharacters) {
    const length = input.value ? input.value.length : 0;
    const remainingCharacters = maxCharacters - length;
    maxCharactersError = remainingCharacters < 0;
    const progress = (1 - (length / maxCharacters)) * 100;

    maxCharacterAdornment = (
      <Tooltip
        title={
          <FormattedMessage
            id="field.text.maxCharacters"
            defaultMessage="{count} of {max} characters remaining"
            values={{count: remainingCharacters, max: maxCharacters}}
          />
        }
      >
        {
          maxCharactersError ?
            <ErrorOutline color={'error'}/> :
            <CircularProgress
              variant={"determinate"}
              value={progress}
              sx={{color: (progress < 10 ? 'warning.main' : 'success.main')}} size={16}
              thickness={8}
            />
        }
      </Tooltip>
    );
  }

  if (startAdornment) {
    startAdornmentComponent = (
      <InputAdornment {...startAdornmentProps} position="start">
        {startAdornment}
      </InputAdornment>
    );
  }

  if (endAdornment || maxCharacterAdornment) {
    endAdornmentComponent = (
      <InputAdornment {...endAdornmentProps} position="end">
        {endAdornment}
        {maxCharacterAdornment}
      </InputAdornment>
    );
  }

  if (submitError || (touched && error)) {
    errorText = error || submitError.join(", ");
  }

  if (isLoading) {
    return <Skeleton variant="rounded" height={54}/>;
  } else {
    return (
      <MUITextField
        fullWidth type="text"
        variant={"outlined"}
        helperText={errorText || customHelperText}
        {...input}
        {...custom}
        error={custom.error || touched && invalid || maxCharactersError}
        slotProps={
          {
            input: {
              startAdornment: startAdornmentComponent,
              ...InputProps,
              endAdornment: (
                <>
                  {endAdornmentComponent}
                  {InputProps?.endAdornment}
                </>
              )
            }
          }
        }
      >
        {children}
      </MUITextField>
    );
  }
};

export {
  TextField,
  ReadonlyTextField,
  SaniQTextFieldProps
};
